import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Upload } from 'lucide-react';
import { useBulkImport } from './hooks';
import { parseVcfBuffer } from './vcardParse';
import { usePageTitle } from '../../hooks/usePageTitle';
import type { ImportVCardResult, ImportVCardRow } from './types';

type Step = 'pick' | 'preview' | 'result';

interface PreviewRow extends ImportVCardRow {
  include: boolean;
  _key: string;
}

export function VCardImportPage() {
  usePageTitle('Import contacts');
  const [step, setStep] = useState<Step>('pick');
  const [parsed, setParsed] = useState<PreviewRow[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [result, setResult] = useState<ImportVCardResult | null>(null);
  const bulk = useBulkImport();

  const totalIncluded = useMemo(() => parsed.filter((p) => p.include).length, [parsed]);

  const onFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const allRows: PreviewRow[] = [];
    const errs: string[] = [];
    for (const file of Array.from(files)) {
      const text = await file.text();
      const { rows, errors } = parseVcfBuffer(text);
      rows.forEach((r, i) =>
        allRows.push({ ...r, include: true, _key: `${file.name}:${i}` })
      );
      errors.forEach((e) =>
        errs.push(`${file.name} card ${e.blockIndex + 1}: ${e.message}`)
      );
    }
    setParsed(allRows);
    setParseErrors(errs);
    setStep(allRows.length > 0 ? 'preview' : 'pick');
  };

  const onSubmit = async () => {
    const toSend = parsed.filter((p) => p.include).map<ImportVCardRow>((p) => ({
      displayName: p.displayName,
      email: p.email,
      phone: p.phone,
      addressLine1: p.addressLine1,
      city: p.city,
      region: p.region,
      postalCode: p.postalCode,
      country: p.country
    }));
    const res = await bulk.mutateAsync(toSend);
    setResult(res);
    setStep('result');
  };

  const onStartOver = () => {
    setParsed([]);
    setParseErrors([]);
    setResult(null);
    setStep('pick');
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      <div>
        <Link to="/customers" className="text-sm text-slate-500 hover:text-sky-600">
          ← Clients
        </Link>
        <h1 className="text-2xl font-semibold text-slate-800 mt-2">Import vCard</h1>
        <p className="text-slate-500 text-sm mt-1">
          Upload one or more <code>.vcf</code> files exported from your phone's Contacts app.
          Contacts are matched against your existing customers by email and phone; exact
          matches are skipped and partial matches are flagged for review.
        </p>
      </div>

      <Stepper current={step} />

      {step === 'pick' && (
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <label
            htmlFor="vcf-input"
            className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-slate-300 rounded-lg px-6 py-12 text-center cursor-pointer hover:border-sky-400 hover:bg-slate-50"
          >
            <Upload size={32} className="text-slate-400" />
            <div className="font-medium text-slate-700">Choose .vcf file(s)</div>
            <div className="text-xs text-slate-500">
              Multiple files OK. Combined .vcf files with many cards also work.
            </div>
            <input
              id="vcf-input"
              type="file"
              accept=".vcf,text/vcard,text/x-vcard"
              multiple
              className="hidden"
              onChange={(e) => onFiles(e.target.files)}
            />
          </label>
          {parseErrors.length > 0 && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
              <p className="font-medium mb-1">Parse errors</p>
              <ul className="list-disc pl-5 space-y-0.5">
                {parseErrors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {step === 'preview' && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-slate-600">
              Parsed <strong>{parsed.length}</strong> card{parsed.length === 1 ? '' : 's'}.
              Uncheck any you don't want to import.
            </p>
            <div className="flex gap-2 text-sm">
              <button
                type="button"
                onClick={() => setParsed((p) => p.map((r) => ({ ...r, include: true })))}
                className="text-sky-600 hover:text-sky-700"
              >
                Select all
              </button>
              <span className="text-slate-300">|</span>
              <button
                type="button"
                onClick={() => setParsed((p) => p.map((r) => ({ ...r, include: false })))}
                className="text-sky-600 hover:text-sky-700"
              >
                Select none
              </button>
            </div>
          </div>

          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 w-10"></th>
                  <th className="text-left px-3 py-2 font-medium">Name</th>
                  <th className="text-left px-3 py-2 font-medium">Email</th>
                  <th className="text-left px-3 py-2 font-medium">Phone</th>
                </tr>
              </thead>
              <tbody>
                {parsed.map((row) => (
                  <tr key={row._key} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={row.include}
                        onChange={(e) =>
                          setParsed((p) =>
                            p.map((r) => (r._key === row._key ? { ...r, include: e.target.checked } : r))
                          )
                        }
                      />
                    </td>
                    <td className="px-3 py-2 text-slate-800">{row.displayName}</td>
                    <td className="px-3 py-2 text-slate-600">{row.email ?? '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{row.phone ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between pt-2">
            <button type="button" onClick={onStartOver} className="text-sm text-slate-600 hover:text-sky-600">
              ← Start over
            </button>
            <button
              type="button"
              onClick={onSubmit}
              disabled={totalIncluded === 0 || bulk.isPending}
              className="bg-sky-600 text-white px-4 py-2 rounded hover:bg-sky-700 disabled:opacity-50"
            >
              {bulk.isPending ? 'Importing…' : `Import ${totalIncluded} contact${totalIncluded === 1 ? '' : 's'}`}
            </button>
          </div>

          {bulk.isError && (
            <p className="text-sm text-red-600">Import failed — please try again.</p>
          )}
        </div>
      )}

      {step === 'result' && result && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Created" value={result.created} tone="green" />
            <Stat label="Duplicate" value={result.duplicate} tone="slate" />
            <Stat label="Possible dupe" value={result.possibleDuplicate} tone="amber" />
            <Stat label="Failed" value={result.failed} tone="red" />
          </div>

          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Name</th>
                  <th className="text-left px-3 py-2 font-medium">Outcome</th>
                  <th className="text-left px-3 py-2 font-medium">Notes</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.map((r) => (
                  <tr key={`${r.sourceIndex}-${r.displayName}`} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-slate-800">{r.displayName}</td>
                    <td className="px-3 py-2">
                      <OutcomeBadge outcome={r.outcome} />
                    </td>
                    <td className="px-3 py-2 text-slate-600 text-xs">
                      {r.outcome === 'Created' && r.customerId && (
                        <Link
                          to={`/customers/${r.customerId}`}
                          className="text-sky-600 hover:text-sky-700"
                        >
                          View →
                        </Link>
                      )}
                      {r.outcome !== 'Created' && (r.reason ?? '—')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {result.possibleDuplicate > 0 && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
              Possible duplicates weren't imported. They share either an email <em>or</em> a
              phone with an existing customer but not both — e.g. family members. If one
              really is new, add them manually from the customer list.
            </p>
          )}

          <div className="flex justify-between pt-2">
            <button type="button" onClick={onStartOver} className="text-sm text-slate-600 hover:text-sky-600">
              Import another file
            </button>
            <Link
              to="/customers"
              className="bg-sky-600 text-white px-4 py-2 rounded hover:bg-sky-700 text-sm"
            >
              Back to customers
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function Stepper({ current }: { current: Step }) {
  const steps: { id: Step; label: string }[] = [
    { id: 'pick', label: '1. Pick file' },
    { id: 'preview', label: '2. Review' },
    { id: 'result', label: '3. Done' }
  ];
  return (
    <ol className="flex gap-2 text-sm">
      {steps.map((s) => {
        const active = s.id === current;
        return (
          <li
            key={s.id}
            className={`px-3 py-1.5 rounded border ${
              active
                ? 'bg-sky-50 border-sky-300 text-sky-800 font-medium'
                : 'bg-white border-slate-200 text-slate-500'
            }`}
          >
            {s.label}
          </li>
        );
      })}
    </ol>
  );
}

function Stat({
  label,
  value,
  tone
}: {
  label: string;
  value: number;
  tone: 'green' | 'slate' | 'amber' | 'red';
}) {
  const palette = {
    green: 'bg-green-50 text-green-800 border-green-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    red: 'bg-red-50 text-red-800 border-red-200'
  }[tone];
  return (
    <div className={`border rounded-lg px-3 py-2 ${palette}`}>
      <div className="text-xs uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-2xl font-semibold">{value}</div>
    </div>
  );
}

function OutcomeBadge({ outcome }: { outcome: ImportVCardRow['displayName'] extends infer _ ? string : never }) {
  const map: Record<string, string> = {
    Created: 'bg-green-50 text-green-800 border-green-200',
    Duplicate: 'bg-slate-100 text-slate-700 border-slate-200',
    PossibleDuplicate: 'bg-amber-50 text-amber-800 border-amber-200',
    Failed: 'bg-red-50 text-red-800 border-red-200'
  };
  const label: Record<string, string> = {
    Created: 'Created',
    Duplicate: 'Duplicate',
    PossibleDuplicate: 'Possible dupe',
    Failed: 'Failed'
  };
  return (
    <span className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded border ${map[outcome] ?? ''}`}>
      {label[outcome] ?? outcome}
    </span>
  );
}
